import { useState } from 'react';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { QueryKeys, TranslationNS } from '@gokrew/models/constants';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input/Input';
import { useDebounce } from '@/hooks/useDebounce';
import { useMessaging } from '@/messaging/useMessaging';
import styles from './MessagingInbox.module.css';

export const RecipientPicker = ({
  onSelected,
}: {
  onSelected: (id: string) => void;
}) => {
  const { t } = useTranslation(TranslationNS.LibsCommon);
  const { api, scope, bootstrap } = useMessaging();
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 250);
  const recipients = useInfiniteQuery({
    queryKey: [QueryKeys.MessagingRecipients, scope, debounced],
    queryFn: ({ pageParam }) =>
      api.recipients({
        search: debounced,
        filter: 'all',
        cursor: pageParam ?? undefined,
        limit: 30,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: !!bootstrap,
    retry: 1,
    throwOnError: false,
  });
  const create = useMutation({
    mutationFn: (id: string) => api.createConversation(id),
    onSuccess: (conversation) => onSelected(conversation.id),
  });
  const people = recipients.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <div className={styles.recipients}>
      <p className={styles.muted}>{t('messaging.picker_intro')}</p>
      <Input
        aria-label={t('messaging.search_label')}
        placeholder={t('messaging.search_recipients')}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      {recipients.isLoading && <p role="status">{t('messaging.loading')}</p>}
      {recipients.isError && (
        <div role="alert">
          <p>{t('messaging.error')}</p>
          <Button
            onClick={() => {
              void recipients.refetch();
            }}
          >
            {t('messaging.retry')}
          </Button>
        </div>
      )}
      {!recipients.isLoading && !recipients.isError && people.length === 0 && (
        <div className={styles.state}>
          <h2>{t('messaging.empty_recipients')}</h2>
          <p>{t('messaging.empty_recipients_body')}</p>
        </div>
      )}
      {people.length > 0 && (
        <p className={styles.muted}>
          {t('messaging.people_count', { count: people.length })}
        </p>
      )}
      {people.map((person) => (
        <button
          className={styles.recipient}
          key={person.userId}
          type="button"
          disabled={!person.available || create.isPending}
          onClick={() => create.mutate(person.userId)}
        >
          <span className={styles.avatar} aria-hidden>
            {person.displayName
              .trim()
              .split(/\s+/)
              .slice(0, 2)
              .map((part) => part[0])
              .join('')}
          </span>
          <div>
            <strong>{person.displayName}</strong>
            <p>{t(`messaging.recipient_role_${person.role}`)}</p>
          </div>
        </button>
      ))}
      {create.isError && (
        <p role="alert" className={styles.error}>
          {t('messaging.error')}
        </p>
      )}
      {recipients.hasNextPage && (
        <Button
          variant="outline"
          disabled={recipients.isFetchingNextPage}
          onClick={() => {
            void recipients.fetchNextPage();
          }}
        >
          {t('messaging.more_people')}
        </Button>
      )}
    </div>
  );
};
